import { useEffect, useRef } from "react";

import {
  getAuthConfig,
  getCurrentUser,
  getTicket,
  isRequestCanceled,
  listAdminClients,
  listAdminDevelopers,
  listNotifications,
  listTickets,
} from "./api";

export function useDashboardData({
  handleLogout,
  selectedTicketIdRef,
  setAdminClients,
  setAdminDevelopers,
  setAdminStatus,
  setDevelopmentTimeMinutes,
  setLoadingData,
  setNotifications,
  setSelectedClientId,
  setSelectedTicket,
  setTickets,
  setUnreadCount,
  setUser,
  token,
  updateSelectedTicketId,
}) {
  const requestIdRef = useRef(0);
  const requestControllerRef = useRef(null);

  const fetchDashboard = async (authToken = token, options = {}) => {
    if (!authToken) return;

    const { background = false, refreshTicketDetail = true } = options;
    if (background && requestControllerRef.current) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;

    const isCurrentRequest = () =>
      requestIdRef.current === requestId && !controller.signal.aborted;

    setLoadingData(true);
    try {
      const headers = getAuthConfig(authToken, { signal: controller.signal });
      const meRes = await getCurrentUser(headers);
      const nextUser = meRes.data;
      const [ticketsRes, notifRes] = await Promise.all([listTickets(headers), listNotifications(headers)]);
      const nextTickets = ticketsRes.data;

      if (!isCurrentRequest()) return;
      setUser(nextUser);
      setTickets(nextTickets);
      setNotifications(notifRes.data.items);
      setUnreadCount(notifRes.data.unread_count);

      if (nextUser.role === "admin") {
        try {
          const clientsRes = await listAdminClients(headers);
          if (!isCurrentRequest()) return;
          setAdminClients(clientsRes.data);
        } catch (error) {
          if (isRequestCanceled(error)) return;
          setAdminClients([]);
        }

        try {
          const developersRes = await listAdminDevelopers(headers);
          if (!isCurrentRequest()) return;
          setAdminDevelopers(developersRes.data);
        } catch (error) {
          if (isRequestCanceled(error)) return;
          setAdminDevelopers([]);
        }
      } else {
        setAdminClients([]);
        setAdminDevelopers([]);
      }

      if (nextUser.role !== "admin") {
        setSelectedClientId("");
      }

      if (nextTickets.length > 0 && refreshTicketDetail) {
        const availableIds = nextTickets.map((ticket) => ticket.id);
        const currentTicketId = selectedTicketIdRef.current;
        const targetId = availableIds.includes(currentTicketId) ? currentTicketId : nextTickets[0].id;
        const detailRes = await getTicket(targetId, headers);
        if (!isCurrentRequest() || selectedTicketIdRef.current !== currentTicketId) return;
        setSelectedTicket(detailRes.data);
        updateSelectedTicketId(detailRes.data.id);
        setAdminStatus(detailRes.data.status);
        setDevelopmentTimeMinutes(detailRes.data.development_time_minutes ?? "");
      } else if (nextTickets.length === 0) {
        setSelectedTicket(null);
        updateSelectedTicketId("");
        setDevelopmentTimeMinutes("");
      }
    } catch (error) {
      if (!isRequestCanceled(error) && isCurrentRequest() && error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      if (isCurrentRequest()) {
        requestControllerRef.current = null;
        setLoadingData(false);
      }
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    fetchDashboard(token);
    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      fetchDashboard(token, {
        background: true,
        refreshTicketDetail: false,
      });
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
      requestIdRef.current += 1;
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
    };
  }, [token]);

  return { fetchDashboard };
}
